import { apiClient, getPlatformUrl } from "../../lib/api";
import {
  SendMessageReq,
  SendMessageRes,
  ChatHistory,
  ChatState,
  UpdateTitleReq,
  UpdateTitleRes,
  ResumeHitlReq,
} from "./type/chat";
import { IAssistantRepo, RagStreamRepoHandlers } from "./irepo";
import { streamRagTurn, streamResumeHitl } from "./sse-client";
import { AssistantCapabilities } from "./type/capabilities";
import { TaskListItem } from "./type/mq-task";
import { LogDownloadReq } from "./type/log";

function buildRagFormData(req: SendMessageReq): FormData {
  const formData = new FormData();
  formData.append("chat_id", req.chat_id);
  formData.append("chat_no", String(req.chat_no));
  formData.append("query", req.query);
  if (req.model_id) formData.append("model_id", req.model_id);
  if (req.tool_execution_role) formData.append("tool_execution_role", req.tool_execution_role);
  if (req.files) req.files.forEach((file) => formData.append("files", file));
  return formData;
}

export class AssistantGateway implements IAssistantRepo {
  async sendMessage(req: SendMessageReq): Promise<SendMessageRes> {
    const url = `${getPlatformUrl()}/xcausal/rag/`;
    const res = await apiClient.post({ url, body: buildRagFormData(req), useToken: true });
    return res as SendMessageRes;
  }

  async sendMessageStream(req: SendMessageReq, handlers: RagStreamRepoHandlers): Promise<void> {
    await streamRagTurn(req, { onEvent: handlers.onEvent }, handlers.signal);
  }

  async resumeHitl(req: ResumeHitlReq): Promise<SendMessageRes> {
    const url = `${getPlatformUrl()}/xcausal/rag/resume`;
    const body: Record<string, unknown> = {
      chat_id: req.chat_id,
      model_id: req.model_id,
      action: req.action,
    };
    if (req.feedback) {
      body.feedback = req.feedback;
    }
    const res = await apiClient.post({ url, body, useToken: true });
    return res as SendMessageRes;
  }

  async resumeHitlStream(req: ResumeHitlReq, handlers: RagStreamRepoHandlers): Promise<void> {
    await streamResumeHitl(req, { onEvent: handlers.onEvent }, handlers.signal);
  }

  async getHistory(): Promise<ChatHistory[]> {
    const url = `${getPlatformUrl()}/xcausal/rag/history`;
    const res = await apiClient.get({ url: url, useToken: true });
    return res as ChatHistory[];
  }

  async getChat(chatId: string): Promise<ChatState> {
    const url = `${getPlatformUrl()}/xcausal/rag/chat?chat_id=${chatId}`;
    const res = await apiClient.get({ url: url, useToken: true });
    return res as ChatState;
  }

  async deleteChat(chatId: string): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/rag/chat?chat_id=${chatId}`;
    const res = await apiClient.remove({ url: url, useToken: true });
    return res;
  }

  async updateChatTitle(chatId: string, req: UpdateTitleReq): Promise<UpdateTitleRes> {
    const url = `${getPlatformUrl()}/xcausal/rag/chat?chat_id=${chatId}`;
    const res = await apiClient.update({ url: url, body: req, useToken: true });
    return res as UpdateTitleRes;
  }

  /** main 版アシスタント（_legacy）が使う。develop-langgraph 版は SSE を使う */
  async fetchTaskList(chatIds: string[]): Promise<TaskListItem[]> {
    const url = `${getPlatformUrl()}/api/message-queue/task/list`;
    const res = await apiClient.post({
      url,
      body: { chat_ids: chatIds, filter_status: ["finished", "error"] },
      useToken: true,
    });
    if (!res) return [];
    return Array.isArray(res) ? (res as TaskListItem[]) : [res as TaskListItem];
  }

  /** main 版アシスタント（_legacy）が使う。develop-langgraph 版は SSE を使う */
  async ackTasks(taskIds: string[]): Promise<string[]> {
    const url = `${getPlatformUrl()}/api/message-queue/task/ack`;
    const res = await apiClient.post({
      url,
      body: { task_ids: taskIds },
      useToken: true,
    });
    return (res?.acknowledged_task_ids ?? []) as string[];
  }

  async downloadArtifact(uri: string, fileExtension: string): Promise<any> {
    const url = `${getPlatformUrl()}/xcausal/rag/artifact/download?uri=${encodeURIComponent(uri)}&file_extension=${fileExtension}`;
    const res = await apiClient.get({ url, useToken: true, responseType: "text" });
    return res;
  }

  /** 利用ログを CSV で取得する（GET /xcausal/rag/client/usage/download） */
  async downloadLog(req: LogDownloadReq): Promise<Blob> {
    const query = new URLSearchParams({
      start_date_utc: req.start_date_utc,
      end_date_utc: req.end_date_utc,
      ...(req.updated_at_tz ? { updated_at_tz: req.updated_at_tz } : {}),
    });
    const url = `${getPlatformUrl()}/xcausal/rag/client/usage/download?${query}`;
    return await apiClient.get({ url, useToken: true, responseType: "blob" });
  }

  async getCapabilities(): Promise<AssistantCapabilities> {
    const url = `${getPlatformUrl()}/xcausal/rag/capabilities`;
    const res = await apiClient.get({ url, useToken: true });
    return res as AssistantCapabilities;
  }
}